// React Component
import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// App Component
import { Tutorial, FloatingTutorialButton } from '../../../components'
import './process-create.css'
import { Routes } from '../../../routes'
import { FamlaServices } from '../../../services'
import {
    createProcess,
    loadProcessIndustries,
    sortProcessIndustries,
    type ProcessCreationType,
    type ProcessIndustry,
} from '../../../services/api/processCreationService'
import { useAppSelector } from '../../../stores'
import { Languages } from '../../../constants'

// Smart Navigation Utility
import { navigateToInterviewWithFallback } from '../../../utils/smartNavigation'
import { getBrandingColor, getBrandingColorHover } from '../../../utils/brandingStyles'

//interface
import { iAlert } from '../../../interfaces'
import { Image, UserRound } from 'lucide-react' // Brain retiré car non utilisé sur /ai
import { useTutorial } from '../../../hooks/useTutorial'
import { processCreateTutorialSteps } from '../../../config/tutorialSteps'

export interface HandoffPrefill {
    name?: string;
    description?: string;
    threadContext?: string;
}

interface ProcessCreateProps {
    handoffPrefill?: HandoffPrefill;
    defaultType?: string;
}


const ProcessCreate = ({ handoffPrefill, defaultType }: ProcessCreateProps = {}) => {
    const { typ } = useParams();
    const type = defaultType?.toUpperCase() || typ?.toUpperCase() || 'AI';
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const navigate = useNavigate()
    const language = localStorage.getItem('currentLanguage') === "fr" ? "french" : "english"
    
    // Tutorial setup
    const tutorialSteps = processCreateTutorialSteps(t);
    const {
        run: runTutorial,
        stepIndex,
        steps,
        startTutorial,
        handleJoyrideCallback,
        isTutorialCompleted,
        resetTutorialProgress
    } = useTutorial(tutorialSteps, 'processCreate');
    const [newProcess, setNewProcess] = React.useState({
        name: '',
        description: '',
        industry: '',
        owner: '',
        isCollaborator: true,
        isSubmit: false,
        myRole: '',
        lang: language
    });


    const [industry, setIndustry] = React.useState<ProcessIndustry[]>([])
    const [stype, setStype] = React.useState<string>(type);
    const [radioOptions, setRadioOptions] = React.useState<{ id: string, value: string, label: string, icon: any }[]>([]);

    const sortedIndustry = sortProcessIndustries(industry);

    const selectIndustryOptions = sortedIndustry.map((industry) => ({
        label: industry.name,
        value: industry.name,
    }));

    const [threadContextOpen, setThreadContextOpen] = React.useState(false);

    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    })

    React.useEffect(() => {
        if (!handoffPrefill) return;

        setNewProcess((prev) => ({
            ...prev,
            name: handoffPrefill.name || prev.name,
            description: handoffPrefill.description || prev.description,
        }));
    }, [handoffPrefill]);

    React.useEffect(() => {

        const getAllIndustry = async () => {
            try {
                setIndustry(await loadProcessIndustries(users.access_token, language));
            } catch {
                setIndustry([]);
            }
        }

        getAllIndustry()

    }, [users.access_token, language])

    React.useEffect(() => {
        const getMyRole = async () => {
            let data = await FamlaServices("api_host", `/users/my/role`, "GET", "", users.access_token);
            if (data.status === 200) {
                setNewProcess((prev) => ({ ...prev, myRole: data.body.data }))
            }
        }
        getMyRole()

    }, [users.access_token])

    const handleChange = (event: any) => {
        const { type, name, value, checked } = event.target;
        let fieldValue = type === 'checkbox' ? checked : value;

        if (type === 'number') {
            fieldValue = Number(fieldValue);
        }
        if (name === 'name') {
            setNewProcess({ ...newProcess, name: fieldValue })
        } else if (name === 'description') {
            setNewProcess({ ...newProcess, description: fieldValue })
        } else if (name === 'selectName') {
            setNewProcess({ ...newProcess, industry: fieldValue })
        } else if (name === 'lang') {
            setNewProcess({ ...newProcess, lang: fieldValue })
        } else {
            setNewProcess({ ...newProcess, owner: fieldValue })
        }
    };

    function checkName(name: string, description: string): boolean {
        if (name === "Test" || name === "test" || description === "Test" || description === "test") {
            setAlert({ isActive: true, type: 'danger', message: t('form.createProcess.nameIsNotValid') });
            return false;
        }

        if (description && description.trim().length < 5) {
            setAlert({ isActive: true, type: 'danger', message: t('form.createProcess.descriptionTooShort') });
            return false;
        }

        return true;
    }


    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        if (newProcess.name.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('form.nameRequired') });
            return;
        }
        if (newProcess.industry.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('form.createProcess.industryRequired') });
            return;
        }
        const selectedIndustry = industry.find(item => item.name === newProcess.industry);
        if (!selectedIndustry) {
            setAlert({ isActive: true, type: 'danger', message: t('form.createProcess.invalidSelection') });
            return;
        }

        const isValid = checkName(newProcess.name, newProcess.description);
        if (!isValid) {
            return;
        }

        setNewProcess({ ...newProcess, isSubmit: true });

        let data = await createProcess({
            title: newProcess.name,
            description: newProcess.description,
            industryId: selectedIndustry._id,
            language: newProcess.lang,
            type: stype as ProcessCreationType,
        }, users.access_token);

        if (data.status === 201) {
            // [NAVIGATION INTELLIGENTE] Utiliser la navigation avec switch automatique après création
            const processId = data.body.data._id;
            setNewProcess({ ...newProcess, name: '', description: '', isSubmit: false });
            
            const success = await navigateToInterviewWithFallback(
                processId,
                users.access_token,
                navigate,
                {
                    onSwitchStart: () => console.log('🔄 [ProcessCreate] Company switch started for new process:', processId),
                    onSwitchComplete: () => console.log('✅ [ProcessCreate] Company switch completed'),
                    onError: (error: string) => {
                        console.error('❌ [ProcessCreate] Smart navigation error:', error);
                        // Fallback vers navigation legacy
                        FamlaServices("api_host", `/interview/process/${processId}`, "GET", "", users.access_token)
                            .then(datas => {
                                if (datas.status === 200) {
                                    navigate(Routes.PROCESS_INTERVIEW + datas.body.data._id);
                                }
                            });
                    }
                }
            );
            
            if (!success) {
                console.warn('⚠️ [ProcessCreate] Smart navigation failed, using legacy method');
                // Fallback vers méthode legacy
                let datas = await FamlaServices("api_host", `/interview/process/${processId}`, "GET", "", users.access_token);
                if (datas.status === 200) {
                    navigate(Routes.PROCESS_INTERVIEW + datas.body.data._id);
                }
            }

        }
        else if (data.body.status_code === 400 && data.body.description === "Process name already exist") {
            setAlert({ isActive: true, type: 'danger', message: t('form.createProcess.processNameAlreadyExist') });

        } else if (data.body.status_code === 401) {
            setAlert({ isActive: true, type: 'danger', message: t('form.createProcess.processCannotCreate') });
        } else if (data.body.status_code === 426) {
            setAlert({ isActive: true, type: 'warning', message: t('form.createProcess.processCreateLimite') });
        }
        else {
            setAlert({ isActive: true, type: 'danger', message: t('navigation.anErrorOcured') });
        }
        setNewProcess({ ...newProcess, isSubmit: false });
    };



    // Met à jour les options radios selon le paramètre `typ`
    React.useEffect(() => {
        // Sur la page /ai : pas d'options radio, on force directement type AI
        if (type === 'AI') {
            setRadioOptions([]);  // Aucune option radio à afficher
            setStype('AI');       // Force le type à AI
            return;
        }

        // Sur la page /human : affichage des options radio (HUMAN + IMAGE)
        const primaryOption = {
            id: 'HUMAN',
            value: 'HUMAN',
            label: 'humanLedInterview',
            icon: UserRound
        };

        const imageOption = {
            id: 'IMAGE',
            value: 'IMAGE',
            label: 'imageBasedMapping',
            icon: Image
        };

        setRadioOptions([primaryOption, imageOption]);
        setStype(primaryOption.value);
    }, [type]);

    // Tutorial reset functions for dev tools
    const resetAllTutorials = () => {
        localStorage.removeItem('tutorial_home_completed');
        localStorage.removeItem('tutorial_processCreate_completed');
        window.location.reload();
    };

    const resetHomeTutorial = () => {
        localStorage.removeItem('tutorial_home_completed');
    };

    const resetProcessCreateTutorial = () => {
        resetTutorialProgress();
        window.location.reload();
    };

    // ------ accent Famla (inchangé clair / sombre) -------
    const ORANGE      = getBrandingColor();
    const ORANGE_DARK = getBrandingColorHover();
    const ORANGE_DIM  = 'var(--pc-orange-dim)';

    /* Champs : fond / bordure / texte via process-create.css + variables --pc-* */
    const sInput: React.CSSProperties = {
        display: 'block',
        width: '100%',
        borderRadius: 10,
        padding: '12px 16px',
        fontSize: 18,
        outline: 'none',
        transition: 'border-color .15s',
    };
    const sLabel: React.CSSProperties = {
        display: 'block',
        fontSize: 15,
        fontWeight: 500,
        color: 'var(--pc-label)',
        marginBottom: 6,
        lineHeight: 1.4,
    };

    const handleFocus  = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        e.currentTarget.style.borderColor = ORANGE;
        e.currentTarget.style.boxShadow   = `0 0 0 1px ${ORANGE}`;
    };
    const handleBlur   = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        e.currentTarget.style.removeProperty('border-color');
        e.currentTarget.style.removeProperty('box-shadow');
    };

    return (
        <div className="pc-wrap" style={{ minHeight: '100vh', background: 'var(--pc-page-bg)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 24px' }}>
            <div style={{ width: '100%', maxWidth: 640 }}>

                {/* ── En-tête ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
                    <Link
                        to={Routes.HOMEVIEW}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 34, height: 34, borderRadius: 8,
                            background: 'var(--pc-surface)', border: '1px solid var(--pc-border)',
                            color: 'var(--pc-label)', textDecoration: 'none', flexShrink: 0,
                            transition: 'color .15s, border-color .15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--pc-back-hover-text)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--pc-border-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--pc-label)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--pc-border)'; }}
                    >
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Link>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 500, color: 'var(--pc-title)' }}>
                        {t('form.createProcess.processOverview')}
                    </h1>
                </div>

                {/* Tutorial anchor */}
                <div className="tutorial-process-create-welcome" style={{ height: 1, visibility: 'hidden' }} />

                {handoffPrefill?.threadContext && (
                    <div
                        style={{
                            border: '1px solid var(--pc-border, #e5e7eb)',
                            borderRadius: 10,
                            marginBottom: 16,
                            overflow: 'hidden',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => setThreadContextOpen((o) => !o)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                padding: '10px 16px',
                                background: 'var(--pc-surface, #f9fafb)',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 500,
                                color: 'var(--pc-label, #374151)',
                            }}
                        >
                            <span>{t('office.handoff.threadContext', 'Office conversation')}</span>
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                style={{
                                    transform: threadContextOpen ? 'rotate(180deg)' : 'rotate(0)',
                                    transition: 'transform .2s',
                                }}
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>
                        {threadContextOpen && (
                            <pre
                                style={{
                                    padding: '12px 16px',
                                    margin: 0,
                                    fontSize: 13,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: 300,
                                    overflowY: 'auto',
                                    color: 'var(--pc-label, #4b5563)',
                                    background: 'var(--pc-surface, #f9fafb)',
                                    borderTop: '1px solid var(--pc-border, #e5e7eb)',
                                }}
                            >
                                {handoffPrefill.threadContext}
                            </pre>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {/* ── Nom du processus ── */}
                    <div data-tutorial="process-name">
                        <label style={sLabel}>
                            {t('views.processByGroupView.processName')}
                            <span style={{ color: '#f87171', marginLeft: 2 }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={newProcess.name}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            placeholder={t('form.createProcess.processNamePlaceholder')}
                            style={sInput}
                        />
                    </div>

                    {/* ── Objectif / Description ── */}
                    <div data-tutorial="process-description">
                        <label style={sLabel}>
                            {t('words.objectifProcess')}
                            <span style={{ color: '#f87171', marginLeft: 2 }}>*</span>
                        </label>
                        <textarea
                            name="description"
                            value={newProcess.description}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            rows={3}
                            placeholder={t('form.createProcess.descriptionPlaceholder')}
                            style={{ ...sInput, resize: 'none' }}
                        />
                    </div>

                    {/* ── Secteur + Langue (2 colonnes) ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div data-tutorial="process-industry">
                            <label style={sLabel}>{t('form.createProcess.selectIndustry')}</label>
                            <select
                                name="selectName"
                                value={newProcess.industry}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                style={{ ...sInput, appearance: 'none', cursor: 'pointer' }}
                            >
                                <option value="" disabled>{t('form.createProcess.selectIndustry')}</option>
                                {selectIndustryOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div data-tutorial="process-language">
                            <label style={sLabel}>{t('form.createProcess.selectLanguage')}</label>
                            <select
                                name="lang"
                                value={newProcess.lang}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                style={{ ...sInput, appearance: 'none', cursor: 'pointer' }}
                            >
                                {Languages.map((lang: { label: string; value: string }) => (
                                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* ── Méthode de saisie — radio cards (route /human uniquement) ── */}
                    {radioOptions.length > 0 && (
                        <div data-tutorial="process-type">
                            <label style={{ ...sLabel, marginBottom: 10 }}>
                                {t('form.createProcess.inputMethod')}
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {radioOptions.map((option) => {
                                    const isChecked = stype === option.value;
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => setStype(option.value)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '12px 16px', borderRadius: 10,
                                                border: `1px solid ${isChecked ? ORANGE : 'var(--pc-border)'}`,
                                                background: isChecked ? ORANGE_DIM : 'var(--pc-surface)',
                                                color: isChecked ? 'var(--pc-radio-checked-text)' : 'var(--pc-label)',
                                                fontSize: 17, fontWeight: 500,
                                                cursor: 'pointer', textAlign: 'left',
                                                transition: 'all .15s',
                                            }}
                                        >
                                            {/* dot indicator */}
                                            <span style={{
                                                flexShrink: 0, width: 16, height: 16, borderRadius: '50%',
                                                border: `2px solid ${isChecked ? ORANGE : 'var(--pc-muted)'}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'border-color .15s',
                                            }}>
                                                {isChecked && (
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: ORANGE }} />
                                                )}
                                            </span>
                                            {option.icon && (
                                                <option.icon size={15} style={{ color: isChecked ? ORANGE : 'var(--pc-muted)', flexShrink: 0 }} />
                                            )}
                                            {t(`form.createProcess.${option.label}`)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Alerte ── */}
                    {alert.isActive && (
                        <div
                            role="alert"
                            style={{
                                borderRadius: 8, padding: '12px 16px', fontSize: 15, fontWeight: 500,
                                background: alert.type === 'danger'
                                    ? 'var(--pc-alert-danger-bg)'
                                    : alert.type === 'warning'
                                      ? 'var(--pc-alert-warning-bg)'
                                      : ORANGE_DIM,
                                border: `1px solid ${
                                    alert.type === 'danger'   ? '#f87171'
                                    : alert.type === 'warning' ? '#fbbf24'
                                    : ORANGE
                                }`,
                                color: alert.type === 'danger'
                                    ? 'var(--pc-alert-danger-text)'
                                    : alert.type === 'warning'
                                      ? 'var(--pc-alert-warning-text)'
                                      : ORANGE,
                            }}
                        >
                            {alert.message}
                        </div>
                    )}

                    {/* ── Bouton Créer ── */}
                    <div data-tutorial="create-button">
                        <button
                            type="submit"
                            disabled={newProcess.isSubmit}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 10,
                                padding: '16px 24px', borderRadius: 12,
                                background: newProcess.isSubmit ? ORANGE_DARK : ORANGE,
                                border: 'none', color: '#fff',
                                fontWeight: 600, fontSize: 19,
                                cursor: newProcess.isSubmit ? 'not-allowed' : 'pointer',
                                opacity: newProcess.isSubmit ? 0.7 : 1,
                                transition: 'background .15s, opacity .15s',
                            }}
                            onMouseEnter={e => { if (!newProcess.isSubmit) (e.currentTarget as HTMLElement).style.background = ORANGE_DARK; }}
                            onMouseLeave={e => { if (!newProcess.isSubmit) (e.currentTarget as HTMLElement).style.background = ORANGE; }}
                        >
                            {newProcess.isSubmit ? (
                                <>
                                    <svg style={{ animation: 'spin 1s linear infinite', width: 16, height: 16 }} viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25" />
                                        <path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                    </svg>
                                    {t('words.loading')}
                                </>
                            ) : (
                                <>
                                    {t('words.create')}
                                    <svg
                                        width={24}
                                        height={24}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden={true}
                                        style={{ flexShrink: 0, marginLeft: 2 }}
                                    >
                                        <path
                                            d="M5 12h14m0 0-5-5m5 5-5 5"
                                            stroke="currentColor"
                                            strokeWidth={2.4}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Tutorial */}
                <Tutorial
                    run={runTutorial}
                    steps={steps}
                    stepIndex={stepIndex}
                    callback={handleJoyrideCallback}
                    continuous={true}
                    showProgress={true}
                    showSkipButton={true}
                />
                <FloatingTutorialButton onClick={startTutorial} />
            </div>
        </div>
    )
}

export default ProcessCreate